import { useState, useEffect, useRef } from 'react';
import QRCode from 'qrcode';
import Icon from 'components/Icon';
import Seo from 'components/Seo';
import ToolSeoContent from 'components/ToolSeoContent';

// QR Code Generator — renders a QR code onto a canvas using the `qrcode`
// library. Runs fully in the browser. User can download as PNG.
export default function QrGenerator() {
  const [text, setText]   = useState('');
  const [size, setSize]   = useState(300);
  const [fg, setFg]       = useState('#1E3A5F');
  const [bg, setBg]       = useState('#FFFFFF');
  const [dataUrl, setDataUrl] = useState('');
  const canvasRef = useRef(null);

  useEffect(() => {
    if (!text.trim()) { setDataUrl(''); return; }
    const canvas = canvasRef.current;
    if (!canvas) return;
    QRCode.toCanvas(
      canvas,
      text,
      { width: size, margin: 2, color: { dark: fg, light: bg } },
      err => {
        if (!err) setDataUrl(canvas.toDataURL('image/png'));
      }
    );
  }, [text, size, fg, bg]);

  return (
    <>
    <Seo title="QR Code Generator Free" description="Create a QR code for any link or text. Download as PNG. Free." path="/tools/utility/qr-code" />
    <div className="tool-pg">
      <div className="container" style={{ maxWidth: 760 }}>
        <div className="tool-hdr">
          <div className="tool-hdr-row">
            <div className="tool-hdr-icon cat-utility"><Icon name="image" size={24} /></div>
            <div>
              <h1>QR Code Generator</h1>
              <p>Create a QR code for any link or text. Customize colors and download as PNG. Free and private.</p>
            </div>
          </div>
        </div>

        <div style={{ display:'grid', gridTemplateColumns:'1fr', gap:20 }}>
          <div className="tool-box">
            <label style={{ fontSize:13, fontWeight:600, color:'var(--slate-700)', display:'block', marginBottom:6 }}>
              Text or URL
            </label>
            <textarea
              className="form-control"
              placeholder="Enter a website link, text, phone number, anything..."
              value={text}
              onChange={e => setText(e.target.value)}
              style={{ minHeight:80, resize:'vertical', fontSize:15, marginBottom:18 }}
            />

            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(140px,1fr))', gap:14, marginBottom:4 }}>
              <div>
                <label style={{ fontSize:13, fontWeight:600, color:'var(--slate-700)', display:'block', marginBottom:6 }}>Size: {size}px</label>
                <input type="range" min="150" max="600" step="10" value={size} onChange={e => setSize(parseInt(e.target.value))} style={{ width:'100%' }} />
              </div>
              <div>
                <label style={{ fontSize:13, fontWeight:600, color:'var(--slate-700)', display:'block', marginBottom:6 }}>QR Color</label>
                <input type="color" value={fg} onChange={e => setFg(e.target.value)} style={{ width:'100%', height:38, border:'1px solid var(--slate-200)', borderRadius:'var(--r-sm)', cursor:'pointer' }} />
              </div>
              <div>
                <label style={{ fontSize:13, fontWeight:600, color:'var(--slate-700)', display:'block', marginBottom:6 }}>Background</label>
                <input type="color" value={bg} onChange={e => setBg(e.target.value)} style={{ width:'100%', height:38, border:'1px solid var(--slate-200)', borderRadius:'var(--r-sm)', cursor:'pointer' }} />
              </div>
            </div>
          </div>

          <div className="tool-box" style={{ textAlign:'center' }}>
            {text.trim() ? (
              <>
                <canvas ref={canvasRef} style={{ maxWidth:'100%', height:'auto', borderRadius:'var(--r)' }} />
                <div style={{ marginTop:16 }}>
                  <a className="btn btn-primary" href={dataUrl} download="qrcode.png">
                    <Icon name="download" size={16} />Download PNG
                  </a>
                </div>
              </>
            ) : (
              <div style={{ padding:'48px 20px', color:'var(--slate-400)' }}>
                <div style={{ width:48, height:48, background:'var(--slate-100)', borderRadius:'var(--r)', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 14px' }}>
                  <Icon name="image" size={22} />
                </div>
                <p>Enter text above to generate your QR code</p>
              </div>
            )}
          </div>
        </div>

        <div className="tool-info">
          <Icon name="info" size={16} />
          <p>QR codes work great for sharing links, WiFi passwords, contact info, or UPI payment links. Everything is generated in your browser — nothing is sent to a server.</p>
        </div>
        <ToolSeoContent tool="qr-code" />
      </div>
    </div>
  </>
  );
}
